import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginState, setLoginState] = useState('init'); // init, scanning, success, error
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  // 检查用户登录状态
  useEffect(() => {
    const checkUser = async () => {
      try {
        // 从本地存储获取用户信息
        const storedUser = localStorage.getItem('relay_user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          setUser(userData);
        }
      } catch (error) {
        console.error('检查用户状态失败:', error);
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, []);

  // 微信登录 - 生成二维码
  const loginWithWechat = async () => {
    setLoading(true);
    setLoginState('scanning');
    try {
      // 在实际应用中，这里会调用微信API生成二维码
      // 模拟生成二维码的过程
      const mockQrCodeUrl = `https://www.weavefox.cn/api/bolt/unsplash_image?keyword=qr&width=300&height=300&random=qr_${Date.now()}`;
      setQrCodeUrl(mockQrCodeUrl);
      
      // 模拟二维码生成后的用户信息获取
      // 在实际应用中，这里会通过轮询或WebSocket等待用户扫码后的回调
      setTimeout(async () => {
        const mockWechatUser = {
          openid: 'wechat_' + Math.random().toString(36).substr(2, 9),
          nickname: '微信用户' + Math.floor(Math.random() * 1000),
          avatar_url: 'https://www.weavefox.cn/api/bolt/unsplash_image?keyword=avatar&width=200&height=200&random=avatar_200_200'
        };

        // 检查用户是否已存在（依据 openid）
        const { data: existingUser, error: fetchError } = await supabase
          .from('users_23813')
          .select('*')
          .eq('openid', mockWechatUser.openid)
          .maybeSingle();

        let userRecord = existingUser;

        if (!existingUser) {
          // 创建新用户（以 openid 为主标识）
          const { data, error: insertError } = await supabase
            .from('users_23813')
            .insert([
              {
                openid: mockWechatUser.openid,
                nickname: mockWechatUser.nickname,
                avatar_url: mockWechatUser.avatar_url
              }
            ])
            .select()
            .single();

          if (insertError) throw insertError;
          userRecord = data;
        } else {
          // 更新最后登录时间
          const { data: updatedUser, error: updateError } = await supabase
            .from('users_23813')
            .update({ last_login: new Date() })
            .eq('id', existingUser.id)
            .select()
            .single();

          if (updateError) throw updateError;
          userRecord = updatedUser;
        }

        // 维护身份桥接映射（wechat）
        const { error: identityErr } = await supabase
          .from('user_identities_23813')
          .upsert({
            user_id: userRecord.id,
            provider: 'wechat',
            provider_user_id: mockWechatUser.openid,
            extra: { nickname: mockWechatUser.nickname, avatar_url: mockWechatUser.avatar_url }
          }, { onConflict: 'provider,provider_user_id' });

        if (identityErr) {
          console.error('创建/更新微信身份映射失败:', identityErr.message);
        }

        // 保存用户信息到状态和本地存储
        setUser(userRecord);
        localStorage.setItem('relay_user', JSON.stringify(userRecord));
        
        setLoginState('success');
        setLoading(false);
        
        return { user: userRecord, error: null };
      }, 2000); // 模拟2秒后登录成功
    } catch (error) {
      console.error('微信登录失败:', error);
      setLoginState('error');
      setLoading(false);
      return { user: null, error: error.message };
    }
  };

  // 登出
  const logout = () => {
    try {
      // 读取当前登录用户用于命名空间清理
      const raw = localStorage.getItem('relay_user');
      let suffix = 'anon';
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          suffix = String(parsed?.id || parsed?.openid || 'anon');
        } catch (_) {
          // noop
        }
      }

      // 清理与该用户相关的本地缓存命名空间（历史与当前计算）
      try { localStorage.removeItem(`JDBH_HISTORY_CACHE:${suffix}`); } catch (_) {}
      try { localStorage.removeItem(`JDBH_CURRENT_CALC_CACHE:${suffix}`); } catch (_) {}

      // 重置状态与本地用户信息
      setUser(null);
      setLoginState('init');
      setQrCodeUrl('');
      localStorage.removeItem('relay_user');
    } catch (_) {
      // 兜底：状态重置与移除本地用户信息
      setUser(null);
      setLoginState('init');
      setQrCodeUrl('');
      try { localStorage.removeItem('relay_user'); } catch (_) {}
    }
  };

  // 刷新二维码
  const refreshQrCode = () => {
    setLoginState('init');
    setQrCodeUrl('');
  };

  return {
    user,
    loading,
    loginState,
    qrCodeUrl,
    loginWithWechat,
    logout,
    refreshQrCode
  };
};
